import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Shield,
  Eye,
  Edit,
  MoreHorizontal,
  FileText,
  Users,
  Link,
  Tag,
  AlertCircle,
  Sparkles,
  TrendingUp,
  RefreshCw,
  CheckCheck,
} from 'lucide-react';
import { researchService } from '@/services/api/research';
import type { QualityAssuranceItem, QualityIssue } from '@/types/research';
import { cn } from '@/lib/utils';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

const STATUS_CONFIG: Record<QualityAssuranceItem['status'], { icon: React.ReactNode; color: string; bgColor: string; label: string }> = {
  pending: {
    icon: <Clock className="h-4 w-4" />,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-500',
    label: 'Pending Review',
  },
  passed: {
    icon: <CheckCircle2 className="h-4 w-4" />,
    color: 'text-green-600',
    bgColor: 'bg-green-500',
    label: 'Passed',
  },
  failed: {
    icon: <XCircle className="h-4 w-4" />,
    color: 'text-red-600',
    bgColor: 'bg-red-500',
    label: 'Failed',
  },
  needs_review: {
    icon: <AlertTriangle className="h-4 w-4" />,
    color: 'text-orange-600',
    bgColor: 'bg-orange-500',
    label: 'Needs Review',
  },
};

const SEVERITY_CONFIG: Record<QualityIssue['severity'], { color: string; bgColor: string }> = {
  low: { color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900' },
  medium: { color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900' },
  high: { color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900' },
};

const COLORS = ['#22c55e', '#eab308', '#f97316', '#ef4444'];

export function ResearchQualityAssurancePage() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedItem, setSelectedItem] = useState<QualityAssuranceItem | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['research-quality-items'],
    queryFn: () => researchService.listQualityItems(),
  });

  const qualityItems = data?.data || [];

  const filteredItems =
    statusFilter === 'all'
      ? qualityItems
      : qualityItems.filter((item) => item.status === statusFilter);

  // Calculate stats
  const passedCount = qualityItems.filter((i) => i.status === 'passed').length;
  const pendingCount = qualityItems.filter((i) => i.status === 'pending').length;
  const needsReviewCount = qualityItems.filter((i) => i.status === 'needs_review').length;
  const failedCount = qualityItems.filter((i) => i.status === 'failed').length;
  const totalIssues = qualityItems.reduce((acc, i) => acc + i.issues.length, 0);
  const avgCompleteness = Math.round(
    qualityItems.reduce((acc, i) => acc + i.metadataCompleteness, 0) / (qualityItems.length || 1)
  );

  const statusDistribution = [
    { name: 'Passed', value: passedCount, color: '#22c55e' },
    { name: 'Pending', value: pendingCount, color: '#eab308' },
    { name: 'Needs Review', value: needsReviewCount, color: '#f97316' },
    { name: 'Failed', value: failedCount, color: '#ef4444' },
  ];

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Quality Assurance</h1>
          <p className="text-muted-foreground">
            Validate metadata completeness and ensure data quality across research resources
          </p>
        </div>
        <Button>
          <RefreshCw className="mr-2 h-4 w-4" />
          Run Validation
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Passed</p>
                <p className="text-2xl font-bold text-green-600">{passedCount}</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-500/10">
                <Clock className="h-5 w-5 text-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Needs Review</p>
                <p className="text-2xl font-bold text-orange-600">{needsReviewCount}</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Failed</p>
                <p className="text-2xl font-bold text-red-600">{failedCount}</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-500/10">
                <XCircle className="h-5 w-5 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Completeness</p>
                <p className="text-2xl font-bold">{avgCompleteness}%</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Shield className="h-5 w-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {statusDistribution.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-muted-foreground">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quality Checks Overview */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Automated Quality Checks
            </CardTitle>
            <CardDescription>
              AI-powered validation ensures data quality and completeness
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-500" />
                    <span className="text-sm">Metadata Completeness</span>
                  </div>
                  <Badge variant="secondary">{avgCompleteness}%</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-purple-500" />
                    <span className="text-sm">Vocabulary Consistency</span>
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    <CheckCheck className="mr-1 h-3 w-3" />
                    92%
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-green-500" />
                    <span className="text-sm">ORCID Linked</span>
                  </div>
                  <Badge variant="secondary">78%</Badge>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Link className="h-4 w-4 text-orange-500" />
                    <span className="text-sm">DOI Validation</span>
                  </div>
                  <Badge variant="secondary">85%</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm">Duplicate Detection</span>
                  </div>
                  <Badge variant="secondary">3 found</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-pink-500" />
                    <span className="text-sm">Overall Quality Score</span>
                  </div>
                  <Badge variant="secondary" className="bg-primary/10 text-primary">
                    87/100
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter & List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Review Queue</CardTitle>
              <CardDescription>Resources pending quality validation</CardDescription>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="needs_review">Needs Review</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="passed">Passed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No items found</h3>
              <p className="text-muted-foreground">
                {statusFilter !== 'all'
                  ? 'No resources match the selected filter'
                  : 'All resources have been validated'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredItems.map((item) => {
                const statusConfig = STATUS_CONFIG[item.status];

                return (
                  <Card key={item.id} className="hover:border-primary/50 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className={cn(
                          'flex h-10 w-10 items-center justify-center rounded-lg flex-shrink-0',
                          `${statusConfig.bgColor}/10`
                        )}>
                          <span className={statusConfig.color}>{statusConfig.icon}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className={statusConfig.color}>
                                  {statusConfig.icon}
                                  <span className="ml-1">{statusConfig.label}</span>
                                </Badge>
                                <Badge variant="secondary">{item.resourceType.replace('_', ' ')}</Badge>
                              </div>
                              <h4 className="font-medium truncate">{item.resourceTitle}</h4>

                              {/* Completeness Bar */}
                              <div className="flex items-center gap-3 mt-2">
                                <span className="text-xs text-muted-foreground">Metadata:</span>
                                <Progress value={item.metadataCompleteness} className="h-2 flex-1 max-w-[200px]" />
                                <span className="text-xs font-medium">{item.metadataCompleteness}%</span>
                              </div>

                              {/* Issues */}
                              {item.issues.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-3">
                                  {item.issues.slice(0, 3).map((issue, index) => (
                                    <Badge
                                      key={index}
                                      variant="outline"
                                      className={cn('text-xs', SEVERITY_CONFIG[issue.severity].color)}
                                    >
                                      <AlertTriangle className="mr-1 h-3 w-3" />
                                      {issue.message.length > 30 ? `${issue.message.slice(0, 30)}...` : issue.message}
                                    </Badge>
                                  ))}
                                  {item.issues.length > 3 && (
                                    <Badge variant="outline" className="text-xs">
                                      +{item.issues.length - 3} more
                                    </Badge>
                                  )}
                                </div>
                              )}

                              {/* Quick Stats */}
                              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  ORCID: {item.orcidLinked ? '✓' : '✗'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Link className="h-3 w-3" />
                                  DOI: {item.doiValid ? '✓' : '✗'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Tag className="h-3 w-3" />
                                  Vocab: {item.vocabularyConsistency ? '✓' : '✗'}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedItem(item)}
                              >
                                <Eye className="mr-2 h-3 w-3" />
                                Review
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem>
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit Resource
                                  </DropdownMenuItem>
                                  <DropdownMenuItem>
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Re-validate
                                  </DropdownMenuItem>
                                  <DropdownMenuItem>
                                    <CheckCircle2 className="mr-2 h-4 w-4" />
                                    Mark as Passed
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedItem && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={STATUS_CONFIG[selectedItem.status].color}>
                    {STATUS_CONFIG[selectedItem.status].icon}
                    <span className="ml-1">{STATUS_CONFIG[selectedItem.status].label}</span>
                  </Badge>
                </div>
                <DialogTitle className="mt-2">{selectedItem.resourceTitle}</DialogTitle>
                <DialogDescription>
                  Quality assessment review for this resource
                </DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="overview" className="mt-4">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="issues">Issues ({selectedItem.issues.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4 mt-4">
                  {/* Completeness */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Metadata Completeness</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4">
                        <Progress value={selectedItem.metadataCompleteness} className="flex-1" />
                        <span className="text-2xl font-bold">{selectedItem.metadataCompleteness}%</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Validation Checks */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Validation Checks</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Vocabulary Consistency</span>
                          <Badge variant={selectedItem.vocabularyConsistency ? 'default' : 'destructive'}>
                            {selectedItem.vocabularyConsistency ? (
                              <><CheckCircle2 className="mr-1 h-3 w-3" /> Passed</>
                            ) : (
                              <><XCircle className="mr-1 h-3 w-3" /> Failed</>
                            )}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">ORCID Linked</span>
                          <Badge variant={selectedItem.orcidLinked ? 'default' : 'secondary'}>
                            {selectedItem.orcidLinked ? (
                              <><CheckCircle2 className="mr-1 h-3 w-3" /> Linked</>
                            ) : (
                              <><AlertTriangle className="mr-1 h-3 w-3" /> Not Linked</>
                            )}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">DOI Valid</span>
                          <Badge variant={selectedItem.doiValid ? 'default' : 'secondary'}>
                            {selectedItem.doiValid ? (
                              <><CheckCircle2 className="mr-1 h-3 w-3" /> Valid</>
                            ) : (
                              <><AlertTriangle className="mr-1 h-3 w-3" /> Not Set</>
                            )}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Duplicate Detection</span>
                          <Badge variant={selectedItem.duplicateDetected ? 'destructive' : 'default'}>
                            {selectedItem.duplicateDetected ? (
                              <><AlertCircle className="mr-1 h-3 w-3" /> Duplicate Found</>
                            ) : (
                              <><CheckCircle2 className="mr-1 h-3 w-3" /> Unique</>
                            )}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Review Info */}
                  {selectedItem.reviewedBy && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Review Information</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Reviewed By</span>
                            <span>{selectedItem.reviewedBy}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Review Date</span>
                            <span>{formatDate(selectedItem.reviewedAt)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="issues" className="mt-4">
                  {selectedItem.issues.length === 0 ? (
                    <Card>
                      <CardContent className="pt-6 text-center">
                        <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
                        <h3 className="font-semibold">No Issues Found</h3>
                        <p className="text-sm text-muted-foreground">
                          This resource passed all quality checks
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {selectedItem.issues.map((issue, index) => (
                        <Card key={index}>
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className={cn(
                                'flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0',
                                SEVERITY_CONFIG[issue.severity].bgColor
                              )}>
                                <AlertTriangle className={cn('h-4 w-4', SEVERITY_CONFIG[issue.severity].color)} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className={SEVERITY_CONFIG[issue.severity].color}>
                                    {issue.severity.toUpperCase()}
                                  </Badge>
                                  <Badge variant="secondary">{issue.type.replace('_', ' ')}</Badge>
                                </div>
                                <p className="text-sm">{issue.message}</p>
                                {issue.field && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Field: <code className="bg-muted px-1 rounded">{issue.field}</code>
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setSelectedItem(null)}>
                  Close
                </Button>
                <Button variant="outline">
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Resource
                </Button>
                {selectedItem.status !== 'passed' && (
                  <Button>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Approve
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
